import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Text, TextInput, TouchableOpacity, View } from "react-native";
import { useTranslation } from "react-i18next";

import { useDevice } from "../_layout";

import Colors from "../../Resources/Color";
import { withAlpha } from "../utils/colorUtils";

// Match selection styling used by pills in ExerciseSearchFloating.js / ExerciseManualPickerModal.js
const PILL_COLORS = {
  exercise: {
    background: "rgba(63, 123, 141, 0.12)",
    text: Colors.bubbleColor,
    border: "rgba(63, 123, 141, 0.18)",
  },
};

const PillButton = ({
  label,
  isMobile,
  selected,
  onPress,
  onPressIn,
  isAdd = false,
  isDisabled = false,
  forceHover = false,
  preventDefaultOnPress = false,
}) => {
  const selectedColors = PILL_COLORS.exercise;
  const [isHovered, setIsHovered] = useState(false);
  const showHover = (isHovered || forceHover) && !selected && !isDisabled;
  const backgroundColor = selected
    ? selectedColors.background
    : showHover
    ? withAlpha(selectedColors.background, 0.65)
    : Colors.whiteColor;
  const textColor = selected
    ? selectedColors.text
    : showHover
    ? withAlpha(selectedColors.text, 0.82)
    : Colors.darkgrayColor;

  return (
    <TouchableOpacity
      onPress={isDisabled ? undefined : onPress}
      onPressIn={isDisabled ? undefined : onPressIn}
      delayPressIn={preventDefaultOnPress ? 90 : 0}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={(e) => {
        if (!preventDefaultOnPress) return;
        e?.preventDefault?.();
      }}
      style={{
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: selected
          ? Colors.bubbleColor
          : showHover
          ? selectedColors.border
          : Colors.borderColor,
        backgroundColor,
        outlineStyle: "none",
        cursor: isDisabled ? "default" : "pointer",
        opacity: isDisabled ? 0.55 : 1,
        maxWidth: "100%",
        flexShrink: 1,
        alignSelf: "flex-start",
      }}
    >
      <Text
        style={{
          fontSize: isAdd ? 14 : 12,
          fontWeight: "500",
          color: textColor,
          lineHeight: 16,
          flexShrink: 1,
          flexWrap: "wrap",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const CreateExerciseComposerPill = React.memo(
  ({
    isMobile,
    value,
    onChangeText,
    placeholder,
    inputRef,
    onSubmit,
    onCancel,
    canSubmit,
    cancelLabel,
    saveLabel,
    autoFocus,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (!isFocused) return;
      if (typeof document === "undefined") return;

      const handler = (e) => {
        const key = e?.key;
        if (key !== "Escape" && key !== "Esc") return;
        e?.preventDefault?.();
        e?.stopPropagation?.();
        onCancel?.();
      };

      document.addEventListener("keydown", handler, true);
      return () => {
        document.removeEventListener("keydown", handler, true);
      };
    }, [isFocused, onCancel]);

    const borderColor = isFocused
      ? withAlpha(Colors.bubbleColor, 0.75)
      : isHovered
      ? withAlpha(Colors.bubbleColor, 0.55)
      : Colors.borderColor;

    const handleKey = useCallback(
      (e) => {
        e?.stopPropagation?.();
        const key = e?.nativeEvent?.key ?? e?.key;

        if (key === "Escape") {
          e?.preventDefault?.();
          onCancel?.();
          return;
        }

        if (key === "Enter") {
          e?.preventDefault?.();
          if (canSubmit) onSubmit?.();
        }
      },
      [canSubmit, onCancel, onSubmit]
    );

    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderRadius: 10,
          borderWidth: 1,
          borderColor,
          backgroundColor: Colors.whiteColor,
          overflow: "hidden",
          maxWidth: "100%",
          flexShrink: 1,
          alignSelf: "flex-start",
        }}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 5,
            flexGrow: 1,
            flexShrink: 1,
          }}
        >
          <TextInput
            ref={inputRef}
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
            placeholderTextColor={Colors.grayColor}
            style={{
              minWidth: 0,
              flexGrow: 1,
              flexShrink: 1,
              fontSize: 12,
              fontWeight: "500",
              color: Colors.darkgrayColor,
              paddingVertical: 0,
              outlineStyle: "none",
            }}
            autoCorrect={false}
            autoCapitalize="none"
            blurOnSubmit={false}
            autoFocus={autoFocus}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onSubmitEditing={() => {
              if (canSubmit) onSubmit?.();
            }}
            onKeyDown={handleKey}
            onKeyPress={handleKey}
            maxLength={65}
          />
        </View>

        <View
          style={{
            width: 1,
            alignSelf: "stretch",
            backgroundColor: Colors.borderColor,
          }}
        />

        <TouchableOpacity
          onPress={onCancel}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 5,
            outlineStyle: "none",
            cursor: "pointer",
            opacity: 0.7,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: "500",
              color: withAlpha(Colors.grayColor, 0.85),
              lineHeight: 16,
              maxWidth: 84,
            }}
            numberOfLines={1}
          >
            {cancelLabel}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={canSubmit ? onSubmit : undefined}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 5,
            outlineStyle: "none",
            cursor: canSubmit ? "pointer" : "default",
            opacity: canSubmit ? 1 : 0.45,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: "700",
              color: Colors.bubbleColor,
              lineHeight: 16,
              maxWidth: 84,
            }}
            numberOfLines={1}
          >
            {saveLabel}
          </Text>
        </TouchableOpacity>
      </View>
    );
  }
);

/**
 * PredefinedExerciseLayout
 *
 * Reusable layout component that renders exercise pills grouped by rows/sections.
 * Each row has a trailing "+" pill to create a new exercise.
 *
 * NOTE: Actions (select/deselect/add) are intentionally placeholders for now.
 */
const PredefinedExerciseLayout = ({
  rows = [],
  selectedExerciseId = null,
  hoveredExerciseId = null,
  onSelectExercise,
  onDeselectExercise,
  onAddExercise,
  onCreateModeChange,
  preventKeyboardDismissOnPress = false,
  onItemPressIn,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  // Keep section order consistent with ExerciseLibrary.
  const EXERCISE_ROW_ORDER = useMemo(
    () => [
      "kneeDominant-string",
      "hipDominant-string",
      "horizontalPush-string",
      "verticalPush-string",
      "horizontalPull-string",
      "verticalPull-string",
      "other-string",
    ],
    []
  );

  // UI rule: Weightlifting is no longer its own category.
  // If callers still provide a "weightlifting-string" row, merge its items into "other-string".
  const normalizedRows = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const weightliftingKey = "weightlifting-string";
    const otherKey = "other-string";

    const getRowKey = (row) => row?.key || row?.title;

    const weightliftingIndex = rows.findIndex(
      (row) => getRowKey(row) === weightliftingKey
    );
    if (weightliftingIndex === -1) return rows;

    const otherIndex = rows.findIndex((row) => getRowKey(row) === otherKey);
    const weightliftingRow = rows[weightliftingIndex];
    const weightliftingItems = Array.isArray(weightliftingRow?.items)
      ? weightliftingRow.items
      : [];

    // If there's no explicit "Other" row, downgrade Weightlifting into Other.
    if (otherIndex === -1) {
      const downgraded = rows
        .map((row, index) => {
          if (index !== weightliftingIndex) return row;
          return {
            ...row,
            key: otherKey,
            title: otherKey,
            items: weightliftingItems,
          };
        })
        .filter(Boolean);

      // Sort rows to match ExerciseLibrary.
      const orderIndex = new Map(EXERCISE_ROW_ORDER.map((k, i) => [k, i]));
      return [...downgraded].sort((a, b) => {
        const ak = getRowKey(a);
        const bk = getRowKey(b);
        const ai = orderIndex.has(ak) ? orderIndex.get(ak) : 999;
        const bi = orderIndex.has(bk) ? orderIndex.get(bk) : 999;
        return ai - bi;
      });
    }

    const otherRow = rows[otherIndex];
    const otherItems = Array.isArray(otherRow?.items) ? otherRow.items : [];

    // Merge Weightlifting into Other.
    // UI rule: Weightlifting items should appear AFTER Other items.
    // If items include an `order` field, sort within each subgroup by `order`.
    const hasOrder = [...otherItems, ...weightliftingItems].some(
      (item) => typeof item?.order === "number" && Number.isFinite(item.order)
    );

    const sortedOtherItems = hasOrder
      ? [...otherItems].sort((a, b) => (a.order || 0) - (b.order || 0))
      : otherItems;
    const sortedWeightliftingItems = hasOrder
      ? [...weightliftingItems].sort((a, b) => (a.order || 0) - (b.order || 0))
      : weightliftingItems;

    const mergedOther = {
      ...otherRow,
      key: otherKey,
      title: otherKey,
      items: [...sortedOtherItems, ...sortedWeightliftingItems],
    };

    const mergedRows = rows
      .map((row, index) => (index === otherIndex ? mergedOther : row))
      .filter((_, index) => index !== weightliftingIndex);

    // Sort rows to match ExerciseLibrary.
    const orderIndex = new Map(EXERCISE_ROW_ORDER.map((k, i) => [k, i]));
    return [...mergedRows].sort((a, b) => {
      const ak = getRowKey(a);
      const bk = getRowKey(b);
      const ai = orderIndex.has(ak) ? orderIndex.get(ak) : 999;
      const bi = orderIndex.has(bk) ? orderIndex.get(bk) : 999;
      return ai - bi;
    });
  }, [rows]);

  const [creatingRowKey, setCreatingRowKey] = useState(null);
  const [draftExerciseName, setDraftExerciseName] = useState("");
  const createInputRef = useRef(null);

  const rowsByKey = useMemo(() => {
    const map = new Map();
    normalizedRows.forEach((row) => {
      const key = row?.key || row?.title;
      if (key) map.set(key, row);
    });
    return map;
  }, [normalizedRows]);

  useEffect(() => {
    if (!creatingRowKey) return;

    const focusTimer = setTimeout(() => {
      try {
        createInputRef.current?.focus?.();
      } catch {
        // no-op
      }
    }, 30);

    return () => clearTimeout(focusTimer);
  }, [creatingRowKey]);

  const handlePressExercise = useCallback(
    (exercise, row) => {
      if (!exercise) return;

      const isSelected = selectedExerciseId === exercise.id;

      if (isSelected) {
        onDeselectExercise?.(exercise, row);
      } else {
        onSelectExercise?.(exercise, row);
      }
    },
    [onDeselectExercise, onSelectExercise, selectedExerciseId]
  );

  const beginCreateForRow = useCallback((row) => {
    const key = row?.key || row?.title;
    if (!key) return;
    setCreatingRowKey(key);
    setDraftExerciseName("");
  }, []);

  const cancelCreate = useCallback(() => {
    setCreatingRowKey(null);
    setDraftExerciseName("");
  }, []);

  useEffect(() => {
    onCreateModeChange?.(
      creatingRowKey
        ? { isCreating: true, cancel: cancelCreate, key: creatingRowKey }
        : { isCreating: false, cancel: null, key: null }
    );
  }, [cancelCreate, creatingRowKey, onCreateModeChange]);

  const submitCreate = useCallback(() => {
    if (!creatingRowKey) return;

    const row = rowsByKey.get(creatingRowKey);
    const name = (draftExerciseName || "").trim();
    if (!row || !name) return;

    onAddExercise?.(row, name);
    setCreatingRowKey(null);
    setDraftExerciseName("");
  }, [creatingRowKey, draftExerciseName, onAddExercise, rowsByKey]);

  return (
    <View style={{ gap: 12 }}>
      {normalizedRows.map((row) => {
        const title = row.title ? t(row.title) : "";
        const items = Array.isArray(row.items) ? row.items : [];
        const rowKey = row?.key || row?.title;
        const isCreatingThisRow = !!rowKey && rowKey === creatingRowKey;
        const canSubmitCreate = (draftExerciseName || "").trim().length > 0;

        return (
          <View key={row.key || row.title} style={{ gap: 8 }}>
            {!!title && (
              <Text
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: Colors.grayColor,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                {title}
              </Text>
            )}

            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
              {items.map((exercise) => (
                <PillButton
                  key={exercise.id}
                  isMobile={isMobile}
                  label={
                    exercise?.nameKey
                      ? t(exercise.nameKey)
                      : exercise?.name
                      ? t(exercise.name)
                      : ""
                  }
                  selected={exercise.id === selectedExerciseId}
                  forceHover={
                    !!hoveredExerciseId && exercise.id === hoveredExerciseId
                  }
                  preventDefaultOnPress={preventKeyboardDismissOnPress}
                  onPressIn={() => {
                    onItemPressIn?.(exercise, row, {
                      isSelected: exercise.id === selectedExerciseId,
                    });
                  }}
                  onPress={() => handlePressExercise(exercise, row)}
                />
              ))}

              {isCreatingThisRow ? (
                <>
                  <CreateExerciseComposerPill
                    isMobile={isMobile}
                    inputRef={createInputRef}
                    value={draftExerciseName}
                    onChangeText={setDraftExerciseName}
                    placeholder={t("exerciseNamePlaceholder-string")}
                    onSubmit={submitCreate}
                    onCancel={cancelCreate}
                    canSubmit={canSubmitCreate}
                    cancelLabel={t("cancel-string")}
                    saveLabel={t("save-string")}
                    autoFocus
                  />
                </>
              ) : (
                <PillButton
                  label={"+"}
                  isMobile={isMobile}
                  isAdd
                  selected={false}
                  preventDefaultOnPress={preventKeyboardDismissOnPress}
                  onPress={() => beginCreateForRow(row)}
                />
              )}
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default PredefinedExerciseLayout;
